import { Link, useLocation } from 'react-router-dom';
import { isComparisonCampaign } from '../../lib/comparisonCampaigns';

export const Footer = () => {
  const location = useLocation();
  const isCampaignPage = isComparisonCampaign(location.pathname);
  const isRoboForexPage = location.pathname === '/roboforex' || isCampaignPage;

  return (
    <footer className="bg-slate-950 text-slate-300 pt-16 pb-8" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          <div>
            <span className="text-lg font-bold tracking-tight text-white font-['Outfit'] block mb-4">
              Markets<span className="text-blue-400">Review</span>
            </span>
            <p className="text-sm text-slate-400 leading-relaxed">
              Independent Forex & CFD broker comparison platform. We provide informational reviews and educational content for traders.
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Brokers</h4>
            <ul className="space-y-2.5">
              <li><Link to="/ic-markets" className="text-sm hover:text-white transition-colors" data-testid="footer-link-ic-markets">IC Markets Review</Link></li>
              <li><Link to="/vantage-markets" className="text-sm hover:text-white transition-colors" data-testid="footer-link-vantage">Vantage Markets Review</Link></li>
              <li><Link to="/xm" className="text-sm hover:text-white transition-colors" data-testid="footer-link-xm">XM Review</Link></li>
              <li><Link to="/roboforex" className="text-sm hover:text-white transition-colors" data-testid="footer-link-roboforex">RoboForex Review</Link></li>
              <li><Link to="/pu-prime" className="text-sm hover:text-white transition-colors" data-testid="footer-link-pu-prime">PU Prime Review</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Education</h4>
            <ul className="space-y-2.5">
              <li><Link to="/guides" className="text-sm hover:text-white transition-colors">All Guides</Link></li>
              <li><Link to="/guides/what-is-a-cfd-broker" className="text-sm hover:text-white transition-colors">What is a CFD Broker?</Link></li>
              <li><Link to="/guides/understanding-trading-risk" className="text-sm hover:text-white transition-colors">Understanding Trading Risk</Link></li>
              <li><Link to="/guides/money-management-for-traders" className="text-sm hover:text-white transition-colors">Money Management</Link></li>
              <li><Link to="/guides/budget-planning-for-new-traders" className="text-sm hover:text-white transition-colors">Budget Planning</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Legal</h4>
            <ul className="space-y-2.5">
              <li><Link to="/privacy-policy" className="text-sm hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms-and-conditions" className="text-sm hover:text-white transition-colors" data-testid="footer-terms">Terms & Conditions</Link></li>
              <li><Link to="/affiliate-disclosure" className="text-sm hover:text-white transition-colors" data-testid="footer-affiliate">Affiliate Disclosure</Link></li>
              <li><Link to="/disclaimer" className="text-sm hover:text-white transition-colors" data-testid="footer-disclaimer">Risk Disclaimer</Link></li>
              <li><Link to="/about" className="text-sm hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="text-sm hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 space-y-4">
          {/* Legal Notices heading */}
          <h4 className="text-sm font-bold text-white uppercase tracking-wider">Legal Notices</h4>

          <div className="text-xs text-slate-500 leading-relaxed space-y-2.5">
            <p data-testid="footer-partner-status">
              <strong className="text-slate-400">Partner Status:</strong> MarketsReview.org is an independent website operated by VIP Affiliate Network Ltd{isRoboForexPage ? ', a RoboForex partner' : ''}. We are not employees, representatives, or an affiliated company of {isRoboForexPage ? 'RoboForex Ltd or ' : ''}any broker listed on this site.
            </p>
            <p data-testid="footer-affiliate-disclosure">
              <strong className="text-slate-400">Affiliate Disclosure:</strong> MarketsReview.org is operated by VIP Affiliate Network Ltd and may receive
              compensation from listed brokers through affiliate partnerships. We are not affiliated with, endorsed by, or officially connected to any broker mentioned.
              {isCampaignPage
                ? ' Featured placement on this comparison page is promotional and does not represent a quality ranking or investment recommendation.'
                : ' This compensation does not influence our editorial content. All brokers are presented with equal visual weight.'}
            </p>
            <p>
              <strong className="text-slate-400">Disclaimer:</strong> The information on this site is for educational and informational purposes only and
              does not constitute financial advice, investment advice, or a recommendation to trade with any particular broker.
            </p>
            {isRoboForexPage && (
            <p>
              <strong className="text-slate-400">RoboForex Ltd:</strong> RoboForex Ltd is a financial brokerage company regulated by the Financial Services Commission (FSC) of Belize. Source: RoboForex official website.
            </p>
            )}
            <p>
              <strong className="text-slate-400">Geographic Restrictions & Exclusive Initiative:</strong> Certain brokers listed on this site do not target clients from the EU/EEA/UK, USA, Canada, Japan, Australia, Iran, Russia, or other restricted jurisdictions. Users are responsible for ensuring that their use of financial services complies with local laws and regulations. You may receive investment services from a third-country firm only at your own exclusive initiative and with full understanding of the risks involved.
            </p>
            <p>
              <strong className="text-slate-400">Trademark & Copyright Notice:</strong> All third-party names, logos, trademarks, and product information displayed on this website are sourced from the respective providers' official websites or publicly available materials and remain the property of their respective owners. Their use on this website is for informational and comparison purposes only. MarketsReview.org does not claim any ownership of these trademarks, logos, or descriptions and is not officially affiliated with, endorsed by, or acting on behalf of any of these institutions unless explicitly stated.
            </p>
          </div>

          {/* Risk Warning - at the very end of legal notices */}
          <div className="bg-red-950/40 border border-red-900/30 rounded-lg p-4">
            <p className="text-xs font-semibold text-red-300 uppercase tracking-wide mb-1">Risk Warning</p>
            <p className="text-xs text-red-200/80 leading-relaxed">
              Trading leveraged financial instruments involves a high level of risk and may not be suitable for all investors. You may lose more than your initial investment. Make sure you have taken independent advice before engaging in any trading activities. CFDs are complex instruments and come with a high risk of losing money rapidly due to leverage. Between 70-80% of retail investor accounts lose money when trading CFDs.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 border-t border-slate-800">
            <p className="text-xs text-slate-500">
              &copy; {new Date().getFullYear()} MarketsReview.org. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-xs text-slate-700 hover:text-slate-400 transition-colors" data-testid="footer-admin-link">Admin</Link>
              <p className="text-xs text-slate-600">
                info@vipaffiliatenetwork.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
